import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  FormControl,
  FormLabel,
  VStack,
  HStack,
  Wrap,
  WrapItem,
  Spacer,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Text,
  Center,
  Spinner,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
} from "@chakra-ui/react";
import {
  ResponsiveContainer,
  ComposedChart,
  BarChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faChartLine,
  faChartBar,
  faTable,
  faClock,
  faMobileScreen,
  faLayerGroup,
} from "@fortawesome/free-solid-svg-icons";
import * as XLSX from "xlsx";
import apiService from "../../services/ApiService";
import { useAuth } from "../../context/AuthContext";
import CustomDatePicker from "../../components/CustomDatePicker";
import { StandardPageTitle, StandardHeader } from "../../themes/StandardHeader";
import { Search, ExportExcel } from "../../themes/IconButtonSet";

const DEVICE_COLORS = ["#3182CE", "#DD6B20", "#38A169", "#805AD5", "#718096"];
const TOP_PAGE_COLORS = [
  "#2B6CB0",
  "#DD6B20",
  "#38A169",
  "#805AD5",
  "#D53F8C",
  "#A0AEC0",
];
const PAGE_GROUP_COLORS = {
  Public: "iBlue.600",
  Farmer: "green.500",
  Staff: "purple.500",
  Unknown: "gray.400",
};

// จำนวนอันดับที่แสดงแยกในกราฟ ส่วนที่เหลือรวมเป็น "อื่นๆ"
const TOP_PAGE_CHART_LIMIT = 5;

const formatNumber = (value) => Number(value || 0).toLocaleString("th-TH");

// แสดงวันที่แบบสั้นสำหรับแกนกราฟ เช่น 25 ก.ค.
const formatShortThaiDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("th-TH", { day: "numeric", month: "short" });
};

const formatFullThaiDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString("th-TH", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const toDateInputValue = (date) => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const StatCard = ({ label, value, helpText, color }) => (
  <Box
    borderWidth={1}
    borderRadius="lg"
    boxShadow="sm"
    p={5}
    borderLeftWidth={6}
    borderLeftColor={color}
  >
    <Stat>
      <StatLabel fontSize="md" color="gray.600">
        {label}
      </StatLabel>
      <StatNumber fontSize="3xl">{formatNumber(value)}</StatNumber>
      {helpText && <StatHelpText mb={0}>{helpText}</StatHelpText>}
    </Stat>
  </Box>
);

const WebsiteVisitStatsDashboard = () => {
  const { sessionId } = useAuth();
  const today = new Date();
  const defaultStartDate = new Date();
  defaultStartDate.setDate(defaultStartDate.getDate() - 29);

  const [filterData, setFilterData] = useState({
    StartDate: toDateInputValue(defaultStartDate),
    EndDate: toDateInputValue(today),
  });
  const [statsData, setStatsData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchStats = useCallback(
    async (range) => {
      setIsLoading(true);
      try {
        const response = await apiService({
          method: "POST",
          url: "/getWebsiteVisitStats",
          data: {
            StartDate: range.StartDate || "",
            EndDate: range.EndDate || "",
          },
          sessionId: sessionId || "",
        });

        if (response.result === "success") {
          setStatsData(response);
        } else {
          setStatsData(null);
          alert("ไม่พบข้อมูล: " + response.error);
        }
      } catch (error) {
        console.error("Error fetching website visit stats:", error);
        alert("เกิดข้อผิดพลาดในการดึงข้อมูล");
      } finally {
        setIsLoading(false);
      }
    },
    [sessionId]
  );

  useEffect(() => {
    fetchStats(filterData);
    // โหลดข้อมูลเริ่มต้น (30 วันย้อนหลัง) ครั้งเดียวตอนเปิดหน้า
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilterData((prev) => ({ ...prev, [name]: value }));
  };

  const applyQuickRange = (days) => {
    const start = new Date();
    start.setDate(start.getDate() - (days - 1));
    const range = {
      StartDate: toDateInputValue(start),
      EndDate: toDateInputValue(new Date()),
    };
    setFilterData(range);
    fetchStats(range);
  };

  const exportToExcel = () => {
    if (!statsData) return;

    const workbook = XLSX.utils.book_new();

    const dailySheet = XLSX.utils.json_to_sheet(
      (statsData.dailyStats || []).map((item) => ({
        วันที่: formatFullThaiDate(item.VisitDate),
        "ผู้เข้าชม (Visitors)": item.Visitors,
        "จำนวนหน้าที่เปิด (Page Views)": item.PageViews,
      }))
    );
    XLSX.utils.book_append_sheet(workbook, dailySheet, "รายวัน");

    const pageSheet = XLSX.utils.json_to_sheet(
      (statsData.topPages || []).map((item, index) => ({
        ลำดับที่: index + 1,
        หน้า: item.PagePath,
        "ผู้เข้าชม (Visitors)": item.Visitors,
        "จำนวนครั้งที่เปิด (Page Views)": item.PageViews,
      }))
    );
    XLSX.utils.book_append_sheet(workbook, pageSheet, "หน้าที่เข้าชมมากที่สุด");

    const groupSheet = XLSX.utils.json_to_sheet(
      (statsData.groupStats || []).map((item) => ({
        ประเภทหน้า: item.PageGroupLabel,
        "ผู้เข้าชม (Visitors)": item.Visitors,
        "จำนวนหน้าที่เปิดชม (Page Views)": item.PageViews,
        "รอบการเข้าชม (Sessions)": item.Sessions,
      }))
    );
    XLSX.utils.book_append_sheet(workbook, groupSheet, "แยกตามประเภทหน้า");

    XLSX.writeFile(workbook, "WebsiteVisitStats.xlsx");
  };

  const summary = statsData?.summary || {};
  const dailyStats = (statsData?.dailyStats || []).map((item) => ({
    ...item,
    DateLabel: formatShortThaiDate(item.VisitDate),
  }));
  const topPages = useMemo(() => statsData?.topPages || [], [statsData]);
  const deviceStats = statsData?.deviceStats || [];
  const hourlyStats = statsData?.hourlyStats || [];
  const groupStats = statsData?.groupStats || [];

  // กราฟหน้าที่ถูกเข้าชมมากที่สุด: แสดง 5 อันดับแรก ที่เหลือรวมเป็น "อื่นๆ"
  const topPagesChartData = useMemo(() => {
    const ranked = topPages.slice(0, TOP_PAGE_CHART_LIMIT).map((page) => ({
      name: page.PagePath,
      value: page.PageViews || 0,
    }));
    const rankedTotal = ranked.reduce((sum, item) => sum + item.value, 0);
    const others = (summary.PageViews || 0) - rankedTotal;

    return others > 0 ? [...ranked, { name: "อื่นๆ", value: others }] : ranked;
  }, [topPages, summary.PageViews]);

  return (
    <Box p={35} m={30} borderWidth={1} borderRadius="lg" boxShadow="lg">
      <StandardPageTitle title="รายงานสถิติการเข้าถึงเว็บไซต์" />

      {/* ฟอร์มฟิลเตอร์ */}
      <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mt={5} mb={6}>
        <StandardHeader
          title="เลือกช่วงเวลา"
          bgColor="iBlue.600"
          icon={faSearch}
        />
        <VStack spacing={6} p={5}>
          <HStack spacing={6} w="100%" flexWrap="wrap">
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                วันที่เริ่มต้น
              </FormLabel>
              <CustomDatePicker
                name="StartDate"
                value={filterData.StartDate}
                onChange={handleInputChange}
                colorScheme="iBlue"
                maxDate={today}
              />
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                วันที่สิ้นสุด
              </FormLabel>
              <CustomDatePicker
                name="EndDate"
                value={filterData.EndDate}
                onChange={handleInputChange}
                colorScheme="iBlue"
                minDate={
                  filterData.StartDate
                    ? new Date(filterData.StartDate)
                    : undefined
                }
                maxDate={today}
              />
            </FormControl>
          </HStack>
          <Box w="100%">
            <Wrap spacing={4} align="center">
              <WrapItem>
                <Button size="sm" variant="outline" onClick={() => applyQuickRange(7)}>
                  7 วันล่าสุด
                </Button>
              </WrapItem>
              <WrapItem>
                <Button size="sm" variant="outline" onClick={() => applyQuickRange(30)}>
                  30 วันล่าสุด
                </Button>
              </WrapItem>
              <WrapItem>
                <Button size="sm" variant="outline" onClick={() => applyQuickRange(90)}>
                  90 วันล่าสุด
                </Button>
              </WrapItem>
              <WrapItem flex="1" />
              <WrapItem>
                <Search
                  onClick={() => fetchStats(filterData)}
                  isLoading={isLoading}
                />
              </WrapItem>
            </Wrap>
          </Box>
        </VStack>
      </Box>

      {isLoading && (
        <Center py={20}>
          <Spinner size="xl" color="iBlue.600" thickness="4px" />
        </Center>
      )}

      {!isLoading && statsData && (
        <VStack spacing={6} align="stretch">
          <Text color="gray.600">
            ข้อมูลระหว่างวันที่ {formatFullThaiDate(statsData.startDate)} ถึง{" "}
            {formatFullThaiDate(statsData.endDate)}
          </Text>

          {/* การ์ดสรุป */}
          <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={5}>
            <StatCard
              label="ผู้เข้าชม (Visitors)"
              value={summary.Visitors}
              helpText="นับผู้เข้าชมที่ไม่ซ้ำกัน"
              color="iBlue.600"
            />
            <StatCard
              label="จำนวนหน้าที่เปิดชม (Page Views)"
              value={summary.PageViews}
              helpText={`เฉลี่ย ${summary.AvgPageViewsPerVisitor || 0} หน้า/คน`}
              color="iOrange.500"
            />
            <StatCard
              label="รอบการเข้าชม (Sessions)"
              value={summary.Sessions}
              helpText={`เฉลี่ย ${summary.AvgPageViewsPerSession || 0} หน้า/รอบ`}
              color="green.500"
            />
            <StatCard
              label="เปิดชมโดยผู้ใช้ที่เข้าสู่ระบบ"
              value={summary.AuthenticatedPageViews}
              helpText={`ผู้เยี่ยมชมทั่วไป ${formatNumber(
                summary.GuestPageViews
              )} ครั้ง`}
              color="purple.500"
            />
          </SimpleGrid>

          {/* แยกตามประเภทของหน้า */}
          <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5}>
            <StandardHeader
              title="การเข้าถึงแยกตามประเภทหน้า"
              bgColor="iBlue.600"
              icon={faLayerGroup}
            />
            <Box p={5}>
              <Text color="gray.600" mb={4}>
                จำนวนหน้าที่เปิดชม (Page Views) แยกตามประเภทของหน้า
              </Text>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={5}>
                {groupStats.map((group) => (
                  <StatCard
                    key={group.PageGroup}
                    label={group.PageGroupLabel}
                    value={group.PageViews}
                    helpText={`ผู้เข้าชม ${formatNumber(
                      group.Visitors
                    )} คน · ${
                      summary.PageViews > 0
                        ? ((group.PageViews / summary.PageViews) * 100).toFixed(1)
                        : "0.0"
                    }% ของการเปิดหน้าทั้งหมด`}
                    color={PAGE_GROUP_COLORS[group.PageGroup] || "gray.400"}
                  />
                ))}
              </SimpleGrid>
            </Box>
          </Box>

          {/* กราฟรายวัน */}
          <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5}>
            <StandardHeader
              title="แนวโน้มการเข้าชมรายวัน"
              bgColor="iBlue.600"
              icon={faChartLine}
            />
            <Box p={5} h="360px">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={dailyStats}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="DateLabel" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(value) => formatNumber(value)} />
                  <Legend />
                  <Bar
                    dataKey="PageViews"
                    name="จำนวนหน้าที่เปิดชม"
                    fill="#90CDF4"
                    barSize={18}
                  />
                  <Line
                    type="monotone"
                    dataKey="Visitors"
                    name="ผู้เข้าชม"
                    stroke="#DD6B20"
                    strokeWidth={2}
                    dot={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          </Box>

          <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={6}>
            {/* สัดส่วนอุปกรณ์ */}
            <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5}>
              <StandardHeader
                title="สัดส่วนตามอุปกรณ์"
                bgColor="green.500"
                icon={faMobileScreen}
              />
              <Box p={5} h="320px">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={deviceStats}
                      dataKey="PageViews"
                      nameKey="DeviceType"
                      innerRadius={60}
                      outerRadius={110}
                      label={(entry) => `${entry.DeviceType}`}
                    >
                      {deviceStats.map((entry, index) => (
                        <Cell
                          key={entry.DeviceType}
                          fill={DEVICE_COLORS[index % DEVICE_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatNumber(value)} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </Box>

            {/* ช่วงเวลาที่มีการเข้าชม */}
            <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5}>
              <StandardHeader
                title="ช่วงเวลาที่มีการเข้าชม"
                bgColor="purple.500"
                icon={faClock}
              />
              <Box p={5} h="320px">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={hourlyStats}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="HourLabel" interval={1} tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(value) => formatNumber(value)} />
                    <Bar
                      dataKey="PageViews"
                      name="จำนวนหน้าที่เปิดชม"
                      fill="#805AD5"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          </SimpleGrid>

          {/* หน้าที่ถูกเข้าชมมากที่สุด */}
          <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5}>
            <StandardHeader
              title="หน้าที่ถูกเข้าชมมากที่สุด"
              bgColor="iOrange.500"
              icon={faChartBar}
            />
            <HStack spacing={5} m={5}>
              <Spacer />
              <ExportExcel onClick={exportToExcel} />
            </HStack>

            {topPagesChartData.length > 0 && (
              <Box p={5} h="360px">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={topPagesChartData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={70}
                      outerRadius={120}
                      label={({ percent }) => `${(percent * 100).toFixed(1)}%`}
                    >
                      {topPagesChartData.map((entry, index) => (
                        <Cell
                          key={entry.name}
                          fill={TOP_PAGE_COLORS[index % TOP_PAGE_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => `${formatNumber(value)} ครั้ง`}
                    />
                    <Legend
                      layout="vertical"
                      align="right"
                      verticalAlign="middle"
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            )}

            <Box p={5} overflowX="auto">
              <TableContainer>
                <Table variant="striped" size="sm">
                  <Thead>
                    <Tr>
                      <Th>
                        <HStack>
                          <FontAwesomeIcon icon={faTable} />
                          <span>ลำดับที่</span>
                        </HStack>
                      </Th>
                      <Th>หน้า</Th>
                      <Th isNumeric>ผู้เข้าชม</Th>
                      <Th isNumeric>จำนวนครั้งที่เปิด</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {topPages.length === 0 && (
                      <Tr>
                        <Td colSpan={4}>
                          <Text textAlign="center" color="gray.500">
                            ไม่พบข้อมูลการเข้าชมในช่วงเวลาที่เลือก
                          </Text>
                        </Td>
                      </Tr>
                    )}
                    {topPages.map((page, index) => (
                      <Tr key={page.PagePath}>
                        <Td>{index + 1}</Td>
                        <Td>{page.PagePath}</Td>
                        <Td isNumeric>{formatNumber(page.Visitors)}</Td>
                        <Td isNumeric>{formatNumber(page.PageViews)}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
        </VStack>
      )}
    </Box>
  );
};

export default WebsiteVisitStatsDashboard;
